/**
 * Centralized constants for the codegen system
 * Replaces magic strings scattered throughout generators
 */

// ============================================================================
// Field Types
// ============================================================================

export const FIELD_TYPES = {
  STRING: 'string',
  EMAIL: 'email',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  TEXT: 'text',
  DECIMAL: 'decimal',
  VARCHAR: 'varchar',
  UUID: 'uuid',
  SERIAL: 'serial',
  DATE: 'date',
  TIMESTAMP: 'timestamp',
  TIME: 'time',
  MARKDOWN: 'markdown',
  RICHTEXT: 'richtext',
  IMAGE: 'image',
  VIDEO: 'video',
  MEDIA: 'media',
  ICON: 'icon',
  GROUP: 'group',
  TABS: 'tabs',
  LIST: 'list',
  SEPARATOR: 'separator',
  SECTION: 'section',
  SELECT: 'select',
  RELATIONSHIP: 'relationship',
  GALLERY: 'gallery'
} as const

export const LAYOUT_FIELD_TYPES = [FIELD_TYPES.SEPARATOR] as const

export const NESTED_FIELD_TYPES = [FIELD_TYPES.GROUP, FIELD_TYPES.TABS, FIELD_TYPES.LIST] as const

export const RICH_TEXT_FIELD_TYPES = [
  FIELD_TYPES.MARKDOWN,
  FIELD_TYPES.RICHTEXT,
  FIELD_TYPES.TEXT
] as const

export const LONG_TEXT_FIELD_TYPES = [
  FIELD_TYPES.TEXT,
  FIELD_TYPES.MARKDOWN,
  FIELD_TYPES.RICHTEXT,
  FIELD_TYPES.LIST,
  FIELD_TYPES.MEDIA,
  FIELD_TYPES.VIDEO,
  FIELD_TYPES.GALLERY
] as const

// ============================================================================
// Column Types
// ============================================================================

export const COLUMN_TYPES = {
  TEXT: 'text',
  BADGE: 'badge',
  DATE: 'date',
  CUSTOM: 'custom',
  AVATAR: 'avatar',
  LINK: 'link',
  IMAGE: 'image',
  EMAIL: 'email',
  NUMBER: 'number',
  BOOLEAN: 'boolean'
} as const

// ============================================================================
// Form Field Types
// ============================================================================

export const FORM_FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  EMAIL: 'email',
  PHONE: 'phone',
  NUMBER: 'number',
  URL: 'url',
  DATE: 'date',
  SELECT: 'select',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  MULTISELECT: 'multiselect',
  FILE: 'file',
  UPLOAD: 'upload',
  GROUP: 'group',
  TIMEZONE: 'timezone',
  LIST: 'list',
  DYNAMIC_FIELDS: 'dynamicFields'
} as const

// ============================================================================
// Auto-generated Fields
// ============================================================================

export const AUTO_FIELDS = {
  ID: 'id',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  PUBLISHED: 'published',
  SLUG: 'slug'
} as const

export const FORM_SUBMISSION_FIELDS = {
  SUBMITTED_AT: 'submittedAt',
  IP_ADDRESS: 'ipAddress',
  USER_AGENT: 'userAgent'
} as const

// ============================================================================
// Database Constants
// ============================================================================

export const DRIZZLE_DEFAULTS = {
  VARCHAR_LENGTH: 255,
  DECIMAL_PRECISION: 10,
  DECIMAL_SCALE: 2
} as const

// ============================================================================
// Generator Names
// ============================================================================

export const CORE_GENERATORS = [
  'database',
  'actions',
  'hooks',
  'columns',
  'table',
  'page',
  'page-content',
  'form',
  'create-page',
  'edit-page',
  'navigation'
] as const

// ============================================================================
// File Extensions
// ============================================================================

export const FILE_EXTENSIONS = {
  TYPESCRIPT: '.ts',
  TSX: '.tsx',
  JSON: '.json',
  SQL: '.sql'
} as const

// ============================================================================
// Type Guards
// ============================================================================

export type FieldTypeValue = (typeof FIELD_TYPES)[keyof typeof FIELD_TYPES]
export type ColumnTypeValue = (typeof COLUMN_TYPES)[keyof typeof COLUMN_TYPES]
export type FormFieldTypeValue = (typeof FORM_FIELD_TYPES)[keyof typeof FORM_FIELD_TYPES]

export function isFieldType(value: string): value is FieldTypeValue {
  return Object.values(FIELD_TYPES).includes(value as FieldTypeValue)
}

export function isColumnType(value: string): value is ColumnTypeValue {
  return Object.values(COLUMN_TYPES).includes(value as ColumnTypeValue)
}

export function isFormFieldType(value: string): value is FormFieldTypeValue {
  return Object.values(FORM_FIELD_TYPES).includes(value as FormFieldTypeValue)
}

export function isLayoutField(type: string): boolean {
  return (LAYOUT_FIELD_TYPES as readonly string[]).includes(type)
}

export function isNestedFieldType(type: string): boolean {
  return (NESTED_FIELD_TYPES as readonly string[]).includes(type)
}

export function isLongTextFieldType(type: string): boolean {
  return (LONG_TEXT_FIELD_TYPES as readonly string[]).includes(type)
}
